/**
 * Party orb strip — diegetic glass chrome at the bottom edge of the
 * canvas. 1-6 orbs, each containing the live BW animated sprite of one
 * party member, in a translucent glass shell with an HP-tinted ring.
 *
 * Step 3 — STRUCTURAL ONLY: static render + lifecycle. No tap, no drag,
 * no flag-based disable yet. Those land in steps 4 + 5 alongside the
 * PartySheetScene and gestureRouter.
 *
 * Lifecycle (matches joystick.ts pattern):
 *   - mountPartyOrbs(scene)            creates the strip container + a
 *                                       Phaser SLEEP/WAKE listener pair
 *   - on SLEEP   → display: none      (kept in DOM; no flicker / re-fetch)
 *   - on WAKE    → display: flex      + refresh from runState (a draft
 *                                       pick / faint may have changed the
 *                                       party while overlay was up)
 *   - handle.destroy()                 final teardown
 *
 * Step 4 will add `setDialogueOpen(open)` + `setModalOverlayOpen(open, source)`
 * flag methods that toggle pointer-events on the container without
 * hiding visuals. Step 9 will subscribe to `runState.onMembersRemoved`
 * for the tombstone state machine.
 *
 * Glass treatment per DESIGN.md / orb-chrome design doc:
 *   - backdrop-filter: blur(12px) saturate(1.4) for bench; saturate(1.6)
 *     for slot 0. Always paired with -webkit-backdrop-filter for Safari
 *     <18 compatibility.
 *   - Inner highlight ring via inset box-shadows. Lead orb's highlight
 *     is intensified (0.45 alpha vs 0.32 for bench) — slot 0 distinction
 *     via light + depth + lift, NOT color. No peach glow halo (Liquid
 *     Glass alignment).
 *   - Top-light radial gradient (white 0.30 alpha center → transparent
 *     at 70%) as the orb's background fill, BENEATH the type-color tint.
 *   - Type-color tint at 0.08 alpha as a second background layer (faint
 *     "the glass took the color of who's inside" lens effect). Resolved
 *     async via @pkmn/dex; falls back to Normal type on cold cache.
 *   - HP ring color thresholds match BattleScene.tweenHpBar exactly:
 *     olive >50% / peach 25-50% / coral ≤25%.
 *
 * a11y: container is role="toolbar" + aria-label="Party". Each orb is
 * role="button" + aria-label="<Name>, Lv <level>, <hpPct>% HP". Tabindex
 * 0 so keyboard nav (Enter/Space) lands here in step 4. Tombstone orbs
 * append ", fainted" in step 9.
 */
import Phaser from 'phaser';
import { runState, type PartyMember } from '../state/runState';
import { saveManager } from '../state/saveManager';
import { loadGen3 } from '../data/dex';
import { SPECIES_TO_DEX } from '../battle/teamFromParty';
import { typeColorRgba } from '../data/typeColors';
import { pokeballGlyphElement } from './pokeballGlyph';
import { Z_PARTY_ORBS } from './zLayers';
import { onLongPress, startDrag } from './gestureRouter';
import {
  ORB_BENCH_SIZE,
  ORB_LEAD_SIZE,
  ORB_LEAD_LIFT,
  SLOT_PITCH,
  STRIP_BOTTOM_INSET,
  hpRingColor,
  computeStripLayout,
  ariaLabelFor,
  type SlotLayout,
} from './partyOrbsLayout';

// Re-export the pure-logic surface so callers can keep importing from
// PartyOrbs.ts as a single module entrypoint. The split is a vitest
// concern (Phaser pulls window at import time) — callers don't need to
// know about it.
export {
  ORB_BENCH_SIZE,
  ORB_LEAD_SIZE,
  ORB_SPRITE_BENCH,
  ORB_SPRITE_LEAD,
  ORB_GAP,
  ORB_LEAD_LIFT,
  SLOT_PITCH,
  STRIP_BOTTOM_INSET,
} from './partyOrbsLayout';

const CONTAINER_ID = 'poke-party-orbs';

// ─── Manifest + types cache ────────────────────────────────────────────

interface ManifestEntry {
  id: number;
  name: string;
  reason: string;
  orbAvailable: boolean;
}

interface Manifest {
  generation: number;
  variant: string;
  orbVariant?: string;
  species: ManifestEntry[];
}

let manifestPromise: Promise<Manifest> | null = null;
function loadManifest(): Promise<Manifest> {
  if (!manifestPromise) {
    manifestPromise = fetch('/assets/pokemon/manifest.json')
      .then((r) => r.json() as Promise<Manifest>)
      .catch((err) => {
        console.warn('[PartyOrbs] manifest fetch failed; assuming orbAvailable=true everywhere', err);
        return { generation: 3, variant: 'unknown', species: [] };
      });
  }
  return manifestPromise;
}

/** species → boolean lookup. Populated lazily from the manifest. While
 *  empty, render assumes orbAvailable=true (the manifest fetch is fast
 *  on a warm cache; the SVG-fallback path is only needed for non-v0.1
 *  species which 99% of v0.1 play never encounters). */
const orbAvailableBySpecies = new Map<string, boolean>();
void loadManifest().then((m) => {
  for (const sp of m.species) {
    orbAvailableBySpecies.set(sp.name, sp.orbAvailable);
  }
});

/** species → primary type lookup, used for the orb backdrop tint. The
 *  dex chunk is eager-warmed at boot (main.ts), so this typically
 *  resolves in microseconds. Cold-cache renders use the Normal tint
 *  fallback and patch in once resolved. */
const typesCache = new Map<string, string[]>();
const typesInflight = new Set<string>();
async function ensureTypes(species: string): Promise<void> {
  if (typesCache.has(species) || typesInflight.has(species)) return;
  typesInflight.add(species);
  try {
    const gen = await loadGen3();
    const sp = gen.species.get(species);
    const types = sp?.types as readonly string[] | undefined;
    typesCache.set(species, types?.slice() ?? []);
  } finally {
    typesInflight.delete(species);
  }
}

// ─── DOM-side helpers (Phaser/DOM only — pure logic lives in
//     partyOrbsLayout.ts so tests can import without dragging in
//     Phaser's window dependency). ──────────────────────────────────

/** Apply glass treatment + size + tint + HP ring to an orb element. Pure
 *  styling — no event listeners. Called both on initial render and any
 *  time the orb needs to update (HP color refresh, type-tint patch). */
function styleOrb(
  el: HTMLDivElement,
  layout: SlotLayout,
  member: PartyMember,
  primaryType: string | undefined,
): void {
  const size = layout.size;
  const ringColor = hpRingColor(member.hp.current, member.hp.max);
  const isLead = layout.isLead;
  // Inner highlight intensity: lead 0.45, bench 0.32. The lead's brighter
  // top-inset shadow is the *only* specular signal that says "active" —
  // no peach halo, no color shift. Light + depth + lift trio.
  const innerHighlightAlpha = isLead ? 0.45 : 0.32;
  const blurPx = 12;
  const saturate = isLead ? 1.6 : 1.4;
  const typeRgba = typeColorRgba(primaryType, 0.08);

  el.style.position = 'absolute';
  el.style.left = `${layout.left}px`;
  el.style.top = `${layout.top}px`;
  el.style.width = `${size}px`;
  el.style.height = `${size}px`;
  el.style.borderRadius = '50%';
  el.style.border = `2px solid ${ringColor}`;
  // Two layered backgrounds:
  //   1. Top-light radial gradient (white center fades to transparent)
  //   2. Solid type-color tint at 0.08 alpha (full circle)
  // Painted in that order so the white top-light reads as a specular
  // hint on top of the species-tinted glass.
  el.style.background = [
    `radial-gradient(circle at 50% 30%, rgba(255, 255, 255, 0.30) 0%, rgba(255, 255, 255, 0) 70%)`,
    typeRgba,
  ].join(', ');
  el.style.boxShadow = [
    `inset 0 1.5px 0 rgba(255, 255, 255, ${innerHighlightAlpha})`,
    `inset 0 -1.5px 0 rgba(0, 0, 0, 0.08)`,
    `0 4px 12px rgba(63, 58, 44, 0.12)`,
  ].join(', ');
  el.style.backdropFilter = `blur(${blurPx}px) saturate(${saturate})`;
  (el.style as unknown as { webkitBackdropFilter: string }).webkitBackdropFilter =
    `blur(${blurPx}px) saturate(${saturate})`;
  el.style.display = 'flex';
  el.style.alignItems = 'center';
  el.style.justifyContent = 'center';
  // overflow MUST stay visible so the canvas depletion overlay (drawn at
  // the orb's outer ring) isn't clipped by the parent's circular bounds.
  // With overflow:hidden the clip happens at the PADDING-box interior,
  // which is INSIDE where the colored CSS border + depletion arc both
  // live — three prior attempts (SVG, CSS-mask, canvas) all failed to
  // render the depletion until this fix because of that clip. The
  // sprite + status pip are both sized + positioned well inside the
  // orb's circle so they don't need the clip.
  el.style.overflow = 'visible';
  el.style.pointerEvents = 'auto';
  el.style.cursor = 'default'; // tap handler lands in step 4 (cursor flips to pointer there)
  el.style.touchAction = 'manipulation';
  el.style.userSelect = 'none';
  (el.style as unknown as { webkitUserSelect: string }).webkitUserSelect = 'none';

  // HP-depletion overlay. The orb's `border` above is the BASELINE
  // (full circle, colored by HP threshold, ALWAYS visible regardless of
  // overlay support). This canvas child paints a dark arc over the
  // DEPLETED portion of the ring, occluding the colored border there →
  // visually the colored ring retracts as HP drops. Canvas chosen over
  // CSS mask + SVG (both prior attempts) because it has no support
  // gating + no element-sizing fallback quirks — every browser since
  // ~2010 renders <canvas>. If the canvas drawing somehow fails, the
  // colored border still shows (worst case = no depletion effect, never
  // worse than baseline).
  let ring = el.querySelector<HTMLCanvasElement>('.party-orb__ring');
  if (!ring) {
    ring = document.createElement('canvas');
    ring.className = 'party-orb__ring';
    ring.style.position = 'absolute';
    ring.style.top = '-2px';
    ring.style.left = '-2px';
    ring.style.pointerEvents = 'none';
    el.insertBefore(ring, el.firstChild);
  }
  const hpPct = member.hp.max > 0
    ? Math.max(0, Math.min(100, (member.hp.current / member.hp.max) * 100))
    : 0;
  drawHpDepletion(ring, size + 4, hpPct);
}

/** Paint the depleted portion of the HP ring onto a per-orb <canvas>.
 *  The orb's CSS border carries the COLOR signal at all times; this
 *  function only draws DARK OCCLUSION over the depleted arc so the
 *  colored border looks like it retracts as HP drops.
 *
 *  Stroke is positioned at canvas radius (center - 1), stroke-width 2,
 *  so it spans pixel radius (center - 2) to (center) — exactly at the
 *  orb's outer edge where the CSS border lives. Color #0E1014 matches
 *  the canvas/body background from index.html so the occluded arc
 *  blends with whatever sits immediately outside the orb's outer edge.
 *
 *  devicePixelRatio scaling so retina screens render crisp 2px strokes
 *  rather than a fuzzy 1.x-px blurry version. */
function drawHpDepletion(
  canvas: HTMLCanvasElement,
  totalSize: number,
  hpPct: number,
): void {
  const dpr = window.devicePixelRatio || 1;
  const targetW = totalSize * dpr;
  const targetH = totalSize * dpr;
  if (canvas.width !== targetW || canvas.height !== targetH) {
    canvas.width = targetW;
    canvas.height = targetH;
    canvas.style.width = `${totalSize}px`;
    canvas.style.height = `${totalSize}px`;
  }
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.scale(dpr, dpr);
  ctx.clearRect(0, 0, totalSize, totalSize);
  // Full HP → no depletion arc drawn → colored border shows uninterrupted.
  if (hpPct >= 100) return;
  const center = totalSize / 2;
  const radius = center - 1;
  // arc() angles go clockwise from positive x-axis (3 o'clock). Subtract
  // π/2 to anchor at 12 o'clock instead. The DEPLETED arc starts where
  // the alive portion ends — at angle hpPct% of full circle — and runs to
  // 2π (the 12 o'clock start point + full circle = back to 12 o'clock).
  const start = (hpPct / 100) * Math.PI * 2 - Math.PI / 2;
  const end = Math.PI * 2 - Math.PI / 2;
  ctx.beginPath();
  ctx.arc(center, center, radius, start, end);
  ctx.lineWidth = 2;
  ctx.strokeStyle = '#0E1014';
  ctx.stroke();
}

/** Build the inner sprite element (BW animated <img> OR SVG fallback).
 *  Returns the element the caller appends. */
function buildSpriteElement(
  member: PartyMember,
  spriteSize: number,
): HTMLElement {
  const dexId = SPECIES_TO_DEX[member.species];
  const orbAvailable =
    dexId !== undefined && (orbAvailableBySpecies.get(member.species) ?? true);

  if (orbAvailable && dexId !== undefined) {
    const img = document.createElement('img');
    img.src = `/assets/pokemon/orb/${dexId}.gif`;
    img.alt = ''; // decorative — aria-label is on the orb button
    img.width = spriteSize;
    img.height = spriteSize;
    img.style.width = `${spriteSize}px`;
    img.style.height = `${spriteSize}px`;
    img.style.imageRendering = 'pixelated';
    img.style.filter = 'drop-shadow(0 1px 0 rgba(0, 0, 0, 0.25))';
    img.style.pointerEvents = 'none';
    // Sprite 404 fallback: swap to the SVG glyph in-place. text-tertiary
    // signals "ghosted placeholder" (DESIGN.md tertiary text role).
    img.onerror = (): void => {
      const fallback = pokeballGlyphElement({
        size: spriteSize,
        color: 'var(--text-tertiary)',
        opacity: 0.85,
      });
      img.replaceWith(fallback);
    };
    return img;
  }

  // Unknown species OR manifest reports orbAvailable=false → SVG glyph.
  return pokeballGlyphElement({
    size: spriteSize,
    color: 'var(--text-tertiary)',
    opacity: 0.85,
  });
}

/** Build the status pip — 8px filled circle inset top-right corner of
 *  the orb, fill-resolved from the --status-* token triad. Returns
 *  null when status is null (no pip rendered). cartridge convention:
 *  'tox' shares 'psn' purple. PR 3 / EXP 5f. */
function buildStatusPip(status: PartyMember['status']): HTMLSpanElement | null {
  if (status === null) return null;
  const tokenName =
    status === 'tox' ? '--status-psn'
    : `--status-${status}`;
  const pip = document.createElement('span');
  pip.className = 'party-orb__status-pip';
  pip.dataset.status = status;
  pip.style.position = 'absolute';
  pip.style.right = '4px';
  pip.style.top = '4px';
  pip.style.width = '8px';
  pip.style.height = '8px';
  pip.style.borderRadius = '50%';
  pip.style.background = `var(${tokenName})`;
  pip.style.boxShadow = '0 0 0 1px rgba(255, 255, 255, 0.4)';
  pip.style.pointerEvents = 'none';
  return pip;
}

/** Pre-fetch each member's orb sprite via a plain `new Image()` so it
 *  warms the BROWSER HTTP cache, not the Phaser texture cache (per eng
 *  review Issue 2 — Phaser's TextureManager doesn't share with the DOM's
 *  fetch cache). When PartyOrbs creates the actual <img>, it hits the
 *  cache instantly. Fire-and-forget. */
function prewarmSprites(party: readonly PartyMember[]): void {
  for (const m of party) {
    const dexId = SPECIES_TO_DEX[m.species];
    if (dexId === undefined) continue;
    const img = new Image();
    img.src = `/assets/pokemon/orb/${dexId}.gif`;
  }
}

// ─── Public mount API ──────────────────────────────────────────────────

export interface PartyOrbsHandle {
  /** Re-read runState + re-render. Called on WAKE; also exposed so
   *  steps 4 + 9 can drive re-renders on flag changes / events. */
  refresh(): void;
  destroy(): void;
  /** Dim + disable interactions (party sheet open, dialogue open,
   *  drag-in-progress on another orb). Visuals stay; pointer-events
   *  go to none so taps fall through.
   *  Idempotent; WorldScene calls it on each setModalOverlayOpen /
   *  setDialogueOpen flip. */
  setInteractionsDisabled(disabled: boolean): void;
}

export function mountPartyOrbs(scene: Phaser.Scene): PartyOrbsHandle {
  const parent = document.getElementById('game') ?? document.body;
  const container = document.createElement('div');
  container.id = CONTAINER_ID;
  container.setAttribute('role', 'toolbar');
  container.setAttribute('aria-label', 'Party');
  container.style.position = 'absolute';
  container.style.left = '0';
  container.style.right = '0';
  // Anchor at the canvas bottom + safe-area inset for iPhones with home
  // indicator. The Math.max keeps a sensible floor on devices without
  // env(safe-area-inset-bottom).
  container.style.bottom = `max(${STRIP_BOTTOM_INSET}px, calc(env(safe-area-inset-bottom) + 8px))`;
  container.style.height = `${ORB_LEAD_SIZE + ORB_LEAD_LIFT}px`;
  container.style.zIndex = String(Z_PARTY_ORBS);
  // Container itself doesn't catch events — only the per-orb children do.
  // This way the joystick zone underneath still receives drags that miss
  // the orbs.
  container.style.pointerEvents = 'none';
  container.style.touchAction = 'none';
  container.style.transition = 'opacity 160ms ease-out';
  parent.appendChild(container);

  /** Whether interactions are currently suppressed (party sheet open,
   *  dialogue open). Used inside render() to set per-orb pointer-events
   *  + the parent's opacity. */
  let interactionsDisabled = false;

  /** Tombstone state. Member ids that runState removed but PartyOrbs is
   *  still rendering as desaturated + ✕. Cleared after the cascade fade
   *  completes. PartyMember data captured at last-render time so we can
   *  still draw the dead orb's sprite + level. */
  const recentlyDead = new Map<string, PartyMember>();
  /** Snapshot of the last rendered party (alive members) keyed by id.
   *  When onMembersRemoved fires, we copy the removed entries from this
   *  cache into `recentlyDead` so we can keep rendering them. */
  let lastSeenById = new Map<string, PartyMember>();
  /** Order the players' party occupied at the moment of death — keeps
   *  tombstones at their original slot positions until the cascade. */
  let lastPartyOrder: string[] = [];
  /** Timer id for the cascade-start delay; null when no cascade
   *  pending. */
  let cascadeStartTimer: ReturnType<typeof setTimeout> | null = null;

  // Cast helper for the cross-scene calls into WorldScene. We mounted
  // from WorldScene specifically, so the methods exist; Phaser.Scene's
  // base type doesn't surface them.
  const worldScene = scene as Phaser.Scene & {
    setModalOverlayOpen?: (open: boolean, source: 'partySheet') => void;
    setDragInProgress?: (active: boolean) => void;
  };

  function launchPartySheet(partyIndex: number): void {
    worldScene.setModalOverlayOpen?.(true, 'partySheet');
    scene.scene.launch('PartySheetScene', { partyIndex });
  }

  /**
   * Begin the long-press-drag-reorder gesture for `orb`. The orb lifts
   * (scale 1.18 + 14 px translateY + soft shadow + z-index bump), drags
   * track 1:1 via translate3d, slot crossings flash + vibrate, and the
   * release commits via runState.reorderParty + save (or springs back
   * on cancel / out-of-strip drop).
   *
   * Design doc spec mirrored exactly:
   *   - 200 ms cubic-out lift on long-press fire
   *   - haptic vibrate(8) on each slot boundary crossing
   *   - target slot flashes white at 0.5 alpha for 80 ms when the
   *     lifted orb's center crosses it
   *   - released > 80 px vertical OR same slot → cancel + spring-back
   *     (220 ms cubic-out)
   *   - committed drop slides into target slot over 200 ms cubic-out
   */
  function beginOrbDrag(orb: HTMLDivElement, startE: PointerEvent): void {
    const fromIdx = Number(orb.dataset.partyIndex);
    if (Number.isNaN(fromIdx)) return;
    const party = runState.getParty();
    const n = party.length;
    if (n <= 1) return; // nothing to reorder

    worldScene.setDragInProgress?.(true);

    // Read the current transform-origin's left so the drag math doesn't
    // double-count layout-level positioning. The orb's CSS position is
    // already absolute; translate3d below stacks on top.
    const startTransform = orb.style.transform;
    const startBoxShadow = orb.style.boxShadow;
    const startZIndex = orb.style.zIndex;

    // Lift the orb. Use CSS transitions for the lift-in animation, then
    // switch to direct transform writes for drag tracking.
    orb.style.transition = 'transform 200ms cubic-bezier(0.33, 1, 0.68, 1), box-shadow 200ms';
    orb.style.zIndex = '30';
    orb.style.transform = 'translate3d(0, -14px, 0) scale(1.18)';
    orb.style.boxShadow = [
      orb.style.boxShadow,
      `0 12px 24px rgba(63, 58, 44, 0.18)`,
    ].join(', ');

    // Drag tracking via window-scoped pointermove. Reads the lifted
    // orb's dx/dy and re-applies the lift baseline on top.
    let lastTargetIdx = fromIdx;
    function clampSlot(idx: number): number {
      return Math.max(0, Math.min(n - 1, idx));
    }
    function computeTargetIdx(dx: number): number {
      return clampSlot(Math.round(fromIdx + dx / SLOT_PITCH));
    }
    function flashSlot(idx: number): void {
      const children = Array.from(container.children) as HTMLElement[];
      const target = children[idx];
      if (!target || target === orb) return;
      // Specular flash — temporary white overlay via inset box-shadow
      // override + a small alpha tween. The flash undoes itself by
      // restoring the previous boxShadow after 80 ms.
      const prev = target.style.boxShadow;
      target.style.transition = 'box-shadow 80ms';
      target.style.boxShadow = `inset 0 0 0 2px rgba(255, 255, 255, 0.5), ${prev}`;
      setTimeout(() => {
        target.style.transition = '';
        target.style.boxShadow = prev;
      }, 80);
    }

    startDrag(startE, {
      onMove: (_e, dx, dy) => {
        // Disable the lift-transition while actively dragging — we want
        // 1:1 follow, not ease-in. Re-enable for the drop animation.
        orb.style.transition = '';
        orb.style.transform = `translate3d(${dx}px, ${dy - 14}px, 0) scale(1.18)`;
        const targetIdx = computeTargetIdx(dx);
        if (targetIdx !== lastTargetIdx) {
          lastTargetIdx = targetIdx;
          flashSlot(targetIdx);
          // Haptic — iOS Safari + Android Chrome both implement
          // navigator.vibrate; desktop Safari is a no-op.
          if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
            navigator.vibrate(8);
          }
        }
      },
      onEnd: (_e, dx, dy, cancelled) => {
        const releasedFar = Math.abs(dy) > 80;
        const targetIdx = computeTargetIdx(dx);
        const shouldCommit = !cancelled && !releasedFar && targetIdx !== fromIdx;

        worldScene.setDragInProgress?.(false);

        if (!shouldCommit) {
          // Spring-back. Don't commit; restore lift-then-rest visuals.
          orb.style.transition =
            'transform 220ms cubic-bezier(0.33, 1, 0.68, 1), box-shadow 220ms';
          orb.style.transform = startTransform;
          orb.style.boxShadow = startBoxShadow;
          // After the animation completes, restore z-index so other
          // orbs can layer normally.
          setTimeout(() => {
            orb.style.zIndex = startZIndex;
            orb.style.transition = '';
          }, 220);
          return;
        }

        // Commit. Re-render kicks in after reorderParty + save; the
        // refreshed orb sits at its new slot's natural position.
        // Cleanup of the lifted orb's transform happens via the
        // refresh's replaceChildren — no need to animate-out.
        runState.reorderParty(fromIdx, targetIdx);
        saveManager.save();
        render();
      },
    });
  }

  function render(): void {
    const party = runState.getParty();
    // Clear + rebuild. Cheap for n≤6 and avoids diffing complexity.
    container.replaceChildren();

    // Refresh the by-id cache from the alive party. Tombstone entries
    // stay in their own map (recentlyDead) and aren't touched.
    lastSeenById = new Map(party.map((m) => [m.id, m]));

    // Build the actual render order. During a tombstone window, we
    // composite recentlyDead entries at their last-known position
    // (lastPartyOrder) so the tombstones sit "where they used to be"
    // and surviving members keep their pre-death slots until the
    // cascade reflow.
    const renderEntries: Array<{ member: PartyMember; isTombstone: boolean }> = [];

    if (recentlyDead.size > 0 && lastPartyOrder.length > 0) {
      // During the tombstone window: iterate lastPartyOrder to preserve
      // slot positions. Tombstoned ids render their cached PartyMember
      // (sprite + level frozen at time of death). Surviving ids pull
      // their live data from runState.getParty().
      for (const id of lastPartyOrder) {
        const tomb = recentlyDead.get(id);
        if (tomb) {
          renderEntries.push({ member: tomb, isTombstone: true });
          continue;
        }
        const live = lastSeenById.get(id);
        if (live) renderEntries.push({ member: live, isTombstone: false });
        // Else: a member that was in lastPartyOrder but isn't in either
        // map (rare). Drop silently.
      }
      // Append any new alive members that weren't in lastPartyOrder
      // (e.g., a draft pick mid-cascade — unusual but possible).
      for (const m of party) {
        if (!lastPartyOrder.includes(m.id)) {
          renderEntries.push({ member: m, isTombstone: false });
        }
      }
    } else {
      // Normal path: just the live party.
      for (const m of party) renderEntries.push({ member: m, isTombstone: false });
      // Update lastPartyOrder so the NEXT tombstone window has accurate
      // pre-death positions to render against.
      lastPartyOrder = party.map((m) => m.id);
    }

    if (renderEntries.length === 0) {
      // Empty party (pre-starter OR full wipe). Strip renders no chrome.
      return;
    }

    prewarmSprites(party);

    const parentWidth = parent.clientWidth || window.innerWidth;
    const layout = computeStripLayout(renderEntries.length, parentWidth);

    for (let i = 0; i < renderEntries.length; i++) {
      const slot = layout[i]!;
      const entry = renderEntries[i]!;
      const member = entry.member;
      const isTombstone = entry.isTombstone;
      const dexId = SPECIES_TO_DEX[member.species];

      const orb = document.createElement('div');
      orb.dataset.partyIndex = String(i);
      orb.dataset.memberId = member.id;
      orb.setAttribute('role', 'button');
      const label = isTombstone
        ? `${ariaLabelFor(member)}, fainted`
        : ariaLabelFor(member);
      orb.setAttribute('aria-label', label);
      // Tombstones aren't keyboard-focusable — the "set as lead" /
      // inspect actions are meaningless on a dead member.
      if (!isTombstone) orb.setAttribute('tabindex', '0');
      orb.dataset.tombstone = isTombstone ? '1' : '0';
      // PR 2 / EXP 7: mark the lead orb so CSS can phase-lock its
      // heartbeat pulse to the HP critical pulse on the bar when
      // #game.is-last-alive-critical is set. Bench orbs don't pulse.
      if (slot.isLead && !isTombstone) orb.dataset.lead = '1';
      // Suppress unused-var when buildSpriteElement doesn't get dexId.
      void dexId;

      // First-pass style with whatever the types cache says (may be
      // empty on cold cache — Normal tint kicks in). After ensureTypes
      // resolves, patch the tint in-place without rebuilding the orb.
      const cachedTypes = typesCache.get(member.species) ?? [];
      styleOrb(orb, slot, member, cachedTypes[0]);
      if (interactionsDisabled || isTombstone) {
        orb.style.pointerEvents = 'none';
        orb.style.cursor = 'default';
      } else {
        orb.style.cursor = 'pointer';
      }
      if (isTombstone) {
        // Desaturate the orb glass + sprite + add ✕ glyph overlay.
        // 0.55 opacity dims the surface so survivors read brighter.
        orb.style.filter = 'grayscale(1)';
        orb.style.opacity = '0.55';
      }

      // Skip interaction handlers on tombstones — no tap (sheet would
      // show stale data), no drag (no slot reorder makes sense).
      if (isTombstone) {
        // ✕ overlay glyph sits centered on the orb.
        const x = document.createElement('span');
        x.textContent = '✕';
        x.style.position = 'absolute';
        x.style.left = '50%';
        x.style.top = '50%';
        x.style.transform = 'translate(-50%, -50%)';
        x.style.fontFamily = 'system-ui, -apple-system, sans-serif';
        x.style.fontSize = '18px';
        x.style.fontWeight = '700';
        x.style.color = 'rgba(63, 58, 44, 0.7)';
        x.style.pointerEvents = 'none';
        orb.appendChild(x);
        const sprite = buildSpriteElement(member, slot.spriteSize);
        orb.appendChild(sprite);
        container.appendChild(orb);
        continue;
      }

      // Tap / long-press / drag — converged into a single gesture pipeline.
      //
      //   pointerdown   → start tracking (tapDown captured)
      //                 → onLongPress's 300 ms hold timer also starts
      //   <300 ms        → release with low drift = TAP, opens
      //                    PartySheetScene
      //   >300 ms hold   → long-press fires, dragStarted = true, the
      //                    follow-up pointerup is consumed by the drag
      //                    pipeline (no tap)
      //   drag drop      → reorderParty(fromIdx, targetIdx) + save +
      //                    refresh (drop outside strip → spring-back,
      //                    no commit)
      //
      // pointerdown / pointerup handlers are bound to the orb element;
      // the onLongPress disposer + startDrag(window-scoped) coexist on
      // the same event stream. Both handlers see the events; whoever's
      // condition fires first wins, the other side defensively bails.
      let tapDown: PointerEvent | null = null;
      let dragStarted = false;

      orb.addEventListener('pointerdown', (e: PointerEvent) => {
        if (interactionsDisabled) return;
        // preventDefault + stopPropagation so the joystick zone's
        // capture-phase listener (which would forward a tap-passthrough
        // event to the canvas) doesn't fire on the same gesture, and so
        // Phaser's input plugin doesn't see this as a world tap.
        e.preventDefault();
        e.stopPropagation();
        tapDown = e;
        dragStarted = false;
      });

      orb.addEventListener('pointerup', (e: PointerEvent) => {
        if (!tapDown || e.pointerId !== tapDown.pointerId) return;
        const downE = tapDown;
        tapDown = null;
        if (dragStarted) return; // drag pipeline owns this — no tap
        if (interactionsDisabled) return;
        const dx = e.clientX - downE.clientX;
        const dy = e.clientY - downE.clientY;
        if (Math.hypot(dx, dy) > 8) return; // drift → not a tap
        if (e.timeStamp - downE.timeStamp > 250) return; // too slow → not a tap
        launchPartySheet(Number(orb.dataset.partyIndex));
      });

      // Long-press → start drag.
      onLongPress(orb, { thresholdMs: 300, driftPx: 8 }, (startE) => {
        if (interactionsDisabled) return;
        dragStarted = true;
        beginOrbDrag(orb, startE);
      });

      // Keyboard activation (Enter/Space) → tap path. Drag is not
      // keyboard-accessible in v1 (documented limitation, TODOs #23).
      orb.addEventListener('keydown', (e: KeyboardEvent) => {
        if (interactionsDisabled) return;
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        launchPartySheet(Number(orb.dataset.partyIndex));
      });

      const sprite = buildSpriteElement(member, slot.spriteSize);
      orb.appendChild(sprite);
      // PR 3 / EXP 5f: status pip (top-right corner). Reads
      // PartyMember.status. Piggybacks the existing onPartyChanged
      // subscription via render() — no new named subscribe method
      // (CLAUDE.md two-method rule + eng review Tension E hold).
      const pip = buildStatusPip(member.status);
      if (pip) orb.appendChild(pip);
      container.appendChild(orb);

      // Async type lookup + tint patch. If already cached, no-op.
      const localOrb = orb;
      const localMember = member;
      const localSlot = slot;
      void ensureTypes(member.species).then(() => {
        // Defensive: the party may have changed by the time the lookup
        // resolves (e.g., a faint during the await). Only patch if the
        // orb is still mounted AND still maps to the same member.
        if (localOrb.dataset.memberId !== localMember.id) return;
        if (!container.contains(localOrb)) return;
        const types = typesCache.get(localMember.species) ?? [];
        styleOrb(localOrb, localSlot, localMember, types[0]);
        // Re-append the sprite — styleOrb's replaceChildren on
        // background/border doesn't disturb children, but the cleared
        // style.background occasionally caused Chromium to drop the
        // child paint until a layout shift. Belt-and-suspenders.
      });
    }
  }

  // SLEEP/WAKE → hide via display, not unmount. Re-fetching the orb
  // sprites on every WAKE would flash. The display flip is cheap.
  const onSleep = (): void => {
    container.style.display = 'none';
  };
  const onWake = (): void => {
    container.style.display = 'block';
    // Party may have changed mid-overlay (draft pick, faint, level-up
    // adjusts HP). Re-render against the fresh state.
    render();
    // Tombstone cascade is keyed to WAKE: if a faint splice happened
    // mid-battle (BattleScene asleep over WorldScene), the tombstone
    // window starts NOW so the player has a chance to parse the death
    // before the cascade reflow.
    maybeStartCascade();
  };
  scene.events.on(Phaser.Scenes.Events.SLEEP, onSleep);
  scene.events.on(Phaser.Scenes.Events.WAKE, onWake);

  // ─── Tombstone state machine (step 9) ──────────────────────────────
  //
  // BattleScene's permadeath splice fires runState.removeWhere on
  // battleEnd → onMembersRemoved emits the ids. WorldScene is asleep
  // during battle, so the cascade timer doesn't START here — it waits
  // for the next WAKE (maybeStartCascade above), giving the player a
  // chance to actually see the orb tombstone before the reflow.

  const offMembersRemoved = runState.onMembersRemoved((ids) => {
    for (const id of ids) {
      // Capture the snapshot from the last-rendered cache. If we
      // haven't rendered this id yet (unusual: removal before first
      // render), skip — there's nothing to draw.
      const snap = lastSeenById.get(id);
      if (snap) recentlyDead.set(id, snap);
    }
    // Render immediately so the tombstone state appears in the
    // current frame. Cascade timer starts on the NEXT WAKE (see
    // maybeStartCascade); if WorldScene is already awake (rare —
    // would mean a non-battle removal like reorderParty splicing
    // wrong), kick the timer now via the same gate.
    if (recentlyDead.size > 0) {
      render();
      // Only start the cascade if WorldScene is currently active;
      // otherwise wait for WAKE.
      if (scene.scene.isActive()) maybeStartCascade();
    }
  });

  const offPartyChanged = runState.onPartyChanged(() => {
    // Re-render against the latest party state. Tombstones layer on
    // top of this via the recentlyDead map.
    render();
  });

  /** Begin the 1.5s settle delay → staggered cascade. Idempotent;
   *  safe to call from both the membersRemoved listener AND the WAKE
   *  handler. */
  function maybeStartCascade(): void {
    if (recentlyDead.size === 0) return;
    if (cascadeStartTimer !== null) return;
    cascadeStartTimer = setTimeout(() => {
      cascadeStartTimer = null;
      runCascade();
    }, 1500);
  }

  /** Stagger-fade each dead orb out (400 ms each, 200 ms gap), then
   *  clear recentlyDead + re-render so survivors reflow into their new
   *  natural slot positions. */
  function runCascade(): void {
    // Snapshot the dead ids in the order they currently appear in the
    // strip so the fade reads left-to-right (matches the player's eye
    // movement across the strip).
    const orderedIds: string[] = lastPartyOrder.filter((id) => recentlyDead.has(id));
    if (orderedIds.length === 0) {
      // Nothing to cascade — clear state and reflow immediately.
      recentlyDead.clear();
      lastPartyOrder = runState.getParty().map((m) => m.id);
      render();
      return;
    }
    orderedIds.forEach((id, idx) => {
      setTimeout(() => {
        const orb = container.querySelector<HTMLDivElement>(
          `[data-member-id="${cssEscapeId(id)}"]`,
        );
        if (orb) {
          // Brief brightness pulse on the ✕ glyph as the orb leaves —
          // the "memorial" beat. Then fade-out 400 ms.
          orb.style.transition = 'opacity 400ms ease-out';
          orb.style.opacity = '0';
        }
        // After this orb's fade ends, if it's the LAST one, clear
        // tombstone state + reflow.
        if (idx === orderedIds.length - 1) {
          setTimeout(() => {
            recentlyDead.clear();
            lastPartyOrder = runState.getParty().map((m) => m.id);
            render();
          }, 400);
        }
      }, idx * 200);
    });
  }

  /** Defensive: data-member-id is a uuid (no special chars in
   *  practice) but document.querySelector requires CSS-escaped
   *  attribute values. Lean on the global if present; fall back to
   *  a minimal escape for stage-only chars. */
  function cssEscapeId(id: string): string {
    const w = window as unknown as { CSS?: { escape?: (s: string) => string } };
    if (typeof w.CSS?.escape === 'function') return w.CSS.escape(id);
    return id.replace(/["\\]/g, '\\$&');
  }

  // Resize handling. visualViewport.resize fires on iOS URL-bar
  // collapse; window.resize covers desktop + Android fallback;
  // orientationchange catches portrait↔landscape (the strip width
  // recenters). rAF-coalesces multiple events fired in the same frame.
  let resizeRaf = 0;
  const onResize = (): void => {
    if (resizeRaf) return;
    resizeRaf = requestAnimationFrame(() => {
      resizeRaf = 0;
      render();
    });
  };
  window.visualViewport?.addEventListener('resize', onResize);
  window.addEventListener('resize', onResize);
  window.addEventListener('orientationchange', onResize);

  // Initial render
  render();

  function applyInteractionsDisabled(): void {
    container.style.opacity = interactionsDisabled ? '0.5' : '1';
    for (const child of Array.from(container.children) as HTMLElement[]) {
      if (interactionsDisabled) {
        child.style.pointerEvents = 'none';
        child.style.cursor = 'default';
      } else {
        child.style.pointerEvents = 'auto';
        child.style.cursor = 'pointer';
      }
    }
  }

  return {
    refresh: render,
    destroy(): void {
      scene.events.off(Phaser.Scenes.Events.SLEEP, onSleep);
      scene.events.off(Phaser.Scenes.Events.WAKE, onWake);
      window.visualViewport?.removeEventListener('resize', onResize);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
      if (resizeRaf) cancelAnimationFrame(resizeRaf);
      offPartyChanged();
      offMembersRemoved();
      if (cascadeStartTimer !== null) clearTimeout(cascadeStartTimer);
      container.remove();
    },
    setInteractionsDisabled(disabled: boolean): void {
      if (interactionsDisabled === disabled) return;
      interactionsDisabled = disabled;
      applyInteractionsDisabled();
    },
  };
}

// Pure logic exports for the tests live in partyOrbsLayout.ts; this
// module is the Phaser/DOM mount surface. Tests don't import from here
// to avoid Phaser's window dependency at module init.
